/*
 * @Author: 张印 hbzyin@163.com
 * @FilePath: /code/mall-fe/src/comps/CategoryBreadcrumb.tsx
 * @Description: 面包屑层级目录展示组件
 *
 */
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import jsurl from 'jsurl';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, ReactNode, useMemo } from 'react';

export interface TreeItem {
  sysNo: string;
  cascadeSysNo: string;
  name: string;
}

type CategoryBreadcrumbProps = {
  data: Array<Category>;
  treeProps: Partial<TreeItem>;
  primaryColor?: string;
  categorySysNoList?: string[];
  isToProductList?: boolean;
  children?: ReactNode;
};

export type Category = {
  level?: number;
  name?: string;
  title?: string;
  id?: string;
  sysNo?: string | number;
  cascadeSysNo?: string;
  cascadeId?: string;
  children: Array<Category>;
  // 前端构建的字段，用于是否显示全部
  isAll?: boolean;
  // 前端构建的字段
  active?: boolean;
  // 前端构建的字段，用于下拉选择
  options?: Array<Category>;
};
/**
 * 封装面包屑导航组件
 * @param {data} param  面包屑导航数据
 * @returns {unknown}
 */
export default function CategoryBreadcrumb({
  data,
  treeProps,
  categorySysNoList,
  primaryColor = 'var(--primaryColor)',
  isToProductList = false,
  children,
}: CategoryBreadcrumbProps) {
  const router = useRouter();
  const query = router.query ?? {};
  const params = jsurl.parse(query.params) ?? {};
  const catId = params?.catId ? decodeURIComponent(params?.catId) : (query.catId as string) || '';
  const catIdArr: Array<string> = useMemo(() => {
    return categorySysNoList?.length ? categorySysNoList : catId?.split(',') ?? [];
  }, [categorySysNoList, catId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderData: Array<Category> = useMemo(() => getRenderData(data), [data, catIdArr]);
  /**
   * 封装面包屑导航数据
   * @param {Object} param  面包屑导航数据
   * @returns {unknown}
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow
  function getRenderData(data) {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const renderData = [];
    const traverse = (list) => {
      if (list?.length) {
        list.forEach((item) => {
          if (catIdArr.includes(item[treeProps.sysNo])) {
            renderData.push({
              ...item,
              options: list?.map((i) => ({
                ...i,
                active: catIdArr.includes(i[treeProps.sysNo]), //设置选中状态
              })),
            });
            if (catId === item[treeProps.cascadeSysNo]) {
              if (item.children?.length) {
                renderData.push({
                  ...item,
                  isAll: true,
                  options: item.children,
                });
              }
            }
            traverse(item.children);
          }
        });
      }
    };
    // 递归面包屑
    traverse(data);
    return renderData;
  }

  function buildLink(item) {
    const param = {
      catId: item[treeProps.cascadeSysNo],
      // 搜索列表,默认进入选中全区域产品
      showAllAddressId: false,
    };
    const newQuery = isToProductList
      ? {
          params: jsurl.stringify(params),
          catId: param.catId,
        }
      : {
          ...query,
          params: jsurl.stringify(param),
        };
    const queryStr = Object.entries(newQuery)
      .map((v) => v.join('='))
      .join('&');
    return isToProductList
      ? `/product/list?${queryStr}`
      : `/pages/shop/pro/list/${query.shopSysNo}?${queryStr}`;
  }
  return (
    <div>
      {children ? (
        <>
          {children}
          <RightOutlined
            style={{
              fontSize: '12px',
              margin: '0 4px',
              verticalAlign: 'middle',
              color: '#babdc2',
            }}
          />
        </>
      ) : null}
      {renderData.map((item, index) => (
        <Fragment key={index}>
          <div className="breadcrumb-item breadcrumb-dropdown">
            <span
              className="breadcrumb-tag--text"
              title={item.isAll ? '全部' : item[treeProps.name]}
            >
              {item.isAll ? '全部' : item[treeProps.name]}
            </span>
            <DownOutlined className="breadcrumb-tag--icon" />
            <div className="category-box">
              {item.options?.map((subItem, subIndex) => (
                <Link href={buildLink(subItem)} key={subIndex} shallow={isToProductList}>
                  <a title={subItem[treeProps.name]} className={subItem.active && 'active'}>
                    {subItem[treeProps.name]}
                  </a>
                </Link>
              ))}
            </div>
          </div>
          {index !== renderData.length - 1 && (
            <RightOutlined
              style={{
                fontSize: '12px',
                margin: '0 4px',
                verticalAlign: 'middle',
                color: '#babdc2',
              }}
            />
          )}
        </Fragment>
      ))}
      <style jsx>{`
        .breadcrumb-item {
          color: inherit;
          height: 24px;
          line-height: 24px;
          vertical-align: middle;
          font-size: 12px;
          max-width: 220px;
          text-overflow: ellipsis;
          overflow: hidden;
          display: inline-block;
        }

        .breadcrumb-item.breadcrumb-dropdown {
          position: relative;
          display: inline-block;
          padding: 0 24px 0 10px;
          height: 24px;
          background-color: #fff;
          border: 1px solid #e8e8e8;
          font-size: 12px;
          color: #333;
          overflow: visible;
          line-height: 22px;
          cursor: pointer;
        }

        .breadcrumb-tag--text {
          margin-right: 10px;
        }

        .breadcrumb-item.breadcrumb-dropdown .breadcrumb-tag--text {
          margin-right: 0;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 170px;
          vertical-align: middle;
        }

        .breadcrumb-item.breadcrumb-dropdown .category-box {
          display: none;
          position: absolute;
          width: 304px;
          height: auto;
          border: 1px solid #e8e8e8;
          background-color: #fff;
          padding: 6px 12px 6px 0;
          left: -1px;
          top: 22px;
          z-index: 9;
          font-size: 0;
          white-space: normal;
        }

        .breadcrumb-item.breadcrumb-dropdown:hover .category-box {
          display: block;
        }

        .category-box > a {
          font-size: 12px;
          display: inline-block;
          vertical-align: top;
          line-height: 26px;
          color: #333;
          margin-left: 12px;
        }

        .breadcrumb-item.breadcrumb-dropdown .category-box > a.active,
        .breadcrumb-item.breadcrumb-dropdown
          .category-box
          > a.category-item:hover,
        .breadcrumb-item.breadcrumb-dropdown .category-box > a:hover {
          color: ${primaryColor};
        }

        .breadcrumb-item :global(.breadcrumb-tag--icon) {
          transition: transform 0.3s;
          position: absolute;
          display: inline-block;
          vertical-align: middle;
          font-size: 8px;
          top: 8px;
          right: 6px;
          color: #999;
        }

        .breadcrumb-item.breadcrumb-dropdown:hover
          :global(.breadcrumb-tag--icon) {
          transform: rotate(-180deg);
        }
      `}</style>
    </div>
  );
}
